// Admin: list + create Help Centres (umbrella sites that group portals as
// cards). Manager-gated; delete is admin-only.
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Plus, Trash, ArrowSquareOut, Gear, ArrowLeft } from '@phosphor-icons/react';
import ServiceDeskUpsell from '../../components/serviceDesk/ServiceDeskUpsell';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function HelpCentresPage() {
  const { getOrgHeaders, currentOrg } = useAuth();
  const { caps } = useSubscription();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [creating, setCreating] = useState(false);

  const sdEnabled = caps?.service_desk_enabled === true;
  const isAdmin = ['owner', 'admin'].includes(currentOrg?.role);

  const load = useCallback(async () => {
    if (!currentOrg || !sdEnabled) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/service-desk/help-centres`, { withCredentials: true, headers: getOrgHeaders() });
      setList(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [currentOrg, sdEnabled, getOrgHeaders]);

  useEffect(() => { load(); }, [load]);

  if (!sdEnabled) return <ServiceDeskUpsell />;

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/service-desk/help-centres`, { name: name.trim(), slug: slug.trim() || undefined }, { withCredentials: true, headers: getOrgHeaders() });
      toast.success(`Help centre "${data.name}" created`);
      setShowCreate(false); setName(''); setSlug('');
      navigate(`/service-desk/help-centres/${data.help_centre_id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create'); }
    finally { setCreating(false); }
  };

  const del = async (hcId) => {
    if (!window.confirm('Delete this help centre? Portals and tickets are not affected.')) return;
    try {
      await axios.delete(`${API}/service-desk/help-centres/${hcId}`, { withCredentials: true, headers: getOrgHeaders() });
      setList(prev => prev.filter(h => h.help_centre_id !== hcId));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div className="flex-1 overflow-auto bg-zinc-50" data-testid="hc-list-page-root">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <button onClick={() => navigate('/service-desk')} className="text-sm text-zinc-500 hover:text-zinc-800 flex items-center gap-1.5 mb-4" data-testid="hc-back">
          <ArrowLeft size={14} /> Back to queue
        </button>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-zinc-900 font-['Outfit']">Help Centres</h1>
            <p className="text-sm text-zinc-500 mt-0.5">A branded site that lists multiple portals as cards. Each portal keeps its own queue.</p>
          </div>
          <Button onClick={() => setShowCreate(v => !v)} data-testid="hc-new-btn"><Plus size={14} className="mr-1.5" /> New help centre</Button>
        </div>

        {showCreate && (
          <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-6 space-y-3" data-testid="hc-create-form">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. AASW Help Centre" data-testid="hc-create-name" />
            </div>
            <div>
              <Label className="text-xs">Slug (URL)</Label>
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto from name (e.g. aasw)" data-testid="hc-create-slug" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={create} disabled={creating} data-testid="hc-create-submit">{creating ? 'Creating…' : 'Create'}</Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-zinc-400">Loading…</p>
        ) : list.length === 0 ? (
          <div className="bg-white border border-dashed border-zinc-300 rounded-lg p-10 text-center text-sm text-zinc-500" data-testid="hc-empty">
            No help centres yet. Click "New help centre" to group your portals into one branded site.
          </div>
        ) : (
          <div className="space-y-3" data-testid="hc-list">
            {list.map(h => (
              <div key={h.help_centre_id} className="bg-white border border-zinc-200 rounded-lg p-4 flex items-center justify-between" data-testid={`hc-row-${h.help_centre_id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-zinc-900">{h.name}</p>
                    {h.is_published
                      ? <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold ring-1 ring-emerald-200">PUBLISHED</span>
                      : <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 text-[10px] font-semibold">DRAFT</span>}
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">/{h.slug} · {h.portal_count} {h.portal_count === 1 ? 'portal' : 'portals'}</p>
                </div>
                <div className="flex items-center gap-2">
                  {h.is_published && (
                    <Button variant="outline" size="sm" onClick={() => window.open(`/helpcentre/${h.slug}`, '_blank')} data-testid={`hc-view-${h.help_centre_id}`}>
                      <ArrowSquareOut size={13} className="mr-1.5" /> View
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => navigate(`/service-desk/help-centres/${h.help_centre_id}`)} data-testid={`hc-manage-${h.help_centre_id}`}>
                    <Gear size={13} className="mr-1.5" /> Manage
                  </Button>
                  {isAdmin && <Button variant="ghost" size="sm" className="text-red-600" onClick={() => del(h.help_centre_id)} data-testid={`hc-delete-${h.help_centre_id}`}><Trash size={13} /></Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
